import {
  ScrollArea,
  Spinner,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "@veritrellis/ui";
import {
  BarChart3,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  LayoutDashboard,
  ListChecks,
  Menu,
  Settings,
  Shield
} from "lucide-react";
import { Link, Navigate, Outlet, useLocation } from "react-router-dom";
import { AdminEnvironmentSwitch } from "../components/AdminEnvironmentSwitch.js";
import { AdminLegalFooter } from "../components/AdminLegalFooter.js";
import { VeritrellisLogo } from "../components/VeritrellisLogo.js";
import { UserAccountMenu } from "../components/UserAccountMenu.js";
import { AdminPageMetaProvider, useAdminPageMetaContext } from "../context/AdminPageMetaContext.js";
import type { WorkspaceRole } from "../hooks/useAdminMe.js";
import { useAccessManifest, type AccessManifestCan } from "../hooks/useAccessManifest.js";
import { roleAtLeast, useCurrentRole } from "../hooks/useCurrentRole.js";
import { useCurrentWorkspace, useWorkspaceId } from "../hooks/useWorkspaceId.js";
import { useDisclosure } from "../hooks/useDisclosure.js";
import { useLocalStorage } from "../hooks/useLocalStorage.js";
import { getAdminRouteMeta, isEnvironmentScopedPath } from "../lib/adminRouteMeta.js";
import { envLabel } from "../lib/requestPresentation.js";
import { usePersistedAdminEnvironment } from "../hooks/usePersistedAdminEnvironment.js";

const COLLAPSED_W = 72;
const EXPANDED_W = 240;
const HEADER_H = 56;

type NavEntry =
  | {
      kind: "link";
      to: string;
      label: string;
      icon: React.ElementType;
      minRole?: WorkspaceRole;
      manifestCan?: AccessManifestCan;
    }
  | { kind: "section"; label: string; minRole?: WorkspaceRole };

const nav: NavEntry[] = [
  { kind: "link", to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { kind: "link", to: "/requests", label: "Requests", icon: ClipboardList },
  { kind: "link", to: "/approvals", label: "Approvals", icon: ListChecks, minRole: "approver" },
  { kind: "link", to: "/policies", label: "Policies", icon: Shield, minRole: "viewer" },
  { kind: "link", to: "/audit", label: "Audit log", icon: BarChart3 },
  { kind: "section", label: "Workspace" },
  { kind: "link", to: "/settings", label: "Settings", icon: Settings }
];

const AdminLayoutInner = () => {
  const [mobileOpen, { toggle: toggleMobile, close: closeMobile }] = useDisclosure();
  const [collapsed, setCollapsed] = useLocalStorage({
    key: "sidebar-collapsed",
    defaultValue: false
  });
  const location = useLocation();
  const { workspaceId, isLoading } = useWorkspaceId();
  const current = useCurrentWorkspace();
  const { role } = useCurrentRole();
  const manifestQ = useAccessManifest(workspaceId);
  const { override: pageMetaOverride } = useAdminPageMetaContext();
  const [environment] = usePersistedAdminEnvironment();

  const visibleNav = nav.filter((item) => {
    if (item.minRole !== undefined && !roleAtLeast(role, item.minRole)) {
      return false;
    }
    if (item.kind === "link" && item.manifestCan !== undefined) {
      if (manifestQ.data?.manifest.can[item.manifestCan] !== true) {
        return false;
      }
    }
    return true;
  });

  if (isLoading) {
    return (
      <div className="admin-auth-loading">
        <Spinner />
      </div>
    );
  }
  if (workspaceId === null) {
    return <Navigate to="/onboarding" replace />;
  }

  const navW = collapsed ? COLLAPSED_W : EXPANDED_W;
  const routeMeta = getAdminRouteMeta(location.pathname);
  const contentMeta = pageMetaOverride ?? routeMeta;
  const showContentChrome = contentMeta !== null;
  const showEnvironmentSwitch =
    isEnvironmentScopedPath(location.pathname) || pageMetaOverride?.environmentScoped === true;

  const isLinkActive = (to: string) =>
    to === "/settings"
      ? location.pathname.startsWith("/settings")
      : location.pathname === to || (to !== "/settings" && location.pathname.startsWith(`${to}/`));

  const renderNavItem = (
    item: NavEntry,
    options: { showLabels: boolean; onNavigate?: () => void }
  ) => {
    if (item.kind === "section") {
      return options.showLabels ? (
        <p key={item.label} className="admin-nav-section-label">
          {item.label}
        </p>
      ) : (
        <div key={item.label} className="admin-nav-section-spacer" aria-hidden />
      );
    }

    const Icon = item.icon;
    const isActive = isLinkActive(item.to);

    if (!options.showLabels) {
      return (
        <Tooltip key={item.to}>
          <TooltipTrigger asChild>
            <Link
              to={item.to}
              onClick={options.onNavigate}
              className={isActive ? "admin-nav-link admin-nav-link--active" : "admin-nav-link"}
              aria-current={isActive ? "page" : undefined}
            >
              <Icon className="admin-nav-icon" strokeWidth={isActive ? 2 : 1.5} />
            </Link>
          </TooltipTrigger>
          <TooltipContent side="right">{item.label}</TooltipContent>
        </Tooltip>
      );
    }

    return (
      <Link
        key={item.to}
        to={item.to}
        onClick={options.onNavigate}
        className={isActive ? "admin-nav-link admin-nav-link--active" : "admin-nav-link"}
        aria-current={isActive ? "page" : undefined}
      >
        <Icon className="admin-nav-icon" strokeWidth={isActive ? 2 : 1.5} />
        <span className="admin-nav-label">{item.label}</span>
      </Link>
    );
  };

  const chromeDescription =
    contentMeta?.description != null && showEnvironmentSwitch
      ? `${contentMeta.description} Viewing ${envLabel(environment)}.`
      : contentMeta?.description;

  return (
    <TooltipProvider>
      <header className="admin-topbar" style={{ height: HEADER_H }}>
        <div className="admin-topbar-inner">
          <div className="admin-topbar-start">
            <button
              type="button"
              className="admin-topbar-icon-btn"
              onClick={toggleMobile}
              aria-label="Open navigation menu"
              aria-expanded={mobileOpen}
            >
              <Menu className="h-5 w-5" />
            </button>
            <div className="admin-topbar-workspace">
              <p className="admin-topbar-workspace-name">{current.workspaceName ?? "Workspace"}</p>
              {current.workspaceSlug !== null ? (
                <p className="admin-topbar-workspace-slug">{current.workspaceSlug}</p>
              ) : null}
            </div>
          </div>

          <div className="admin-topbar-end">
            {showEnvironmentSwitch ? <AdminEnvironmentSwitch /> : null}
            <UserAccountMenu />
          </div>
        </div>
      </header>

      <aside
        className="admin-sidebar"
        style={{
          top: HEADER_H,
          width: navW
        }}
      >
        <div className="admin-sidebar-brand">
          <Link to="/dashboard" className="admin-sidebar-logo-link">
            {collapsed ? (
              <img
                src="/logo-icon.svg"
                alt="Veritrellis"
                height={28}
                style={{ height: 28, width: "auto" }}
              />
            ) : (
              <VeritrellisLogo height={26} />
            )}
          </Link>
        </div>

        <ScrollArea className="admin-sidebar-nav">
          <nav className="admin-sidebar-nav-inner" aria-label="Main">
            {visibleNav.map((item) => renderNavItem(item, { showLabels: !collapsed }))}
          </nav>
        </ScrollArea>

        <div className="admin-sidebar-footer">
          <button
            type="button"
            className="admin-sidebar-collapse-btn"
            onClick={() => setCollapsed(!collapsed)}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {collapsed ? (
              <ChevronRight className="h-4 w-4" strokeWidth={2} />
            ) : (
              <>
                <ChevronLeft className="h-4 w-4" strokeWidth={2} />
                <span className="admin-sidebar-collapse-label">Collapse</span>
              </>
            )}
          </button>
        </div>
      </aside>

      <main
        className="admin-shell"
        style={{
          paddingTop: HEADER_H,
          paddingLeft: navW
        }}
      >
        <div className="admin-content-pane">
          {showContentChrome ? (
            <header className="admin-content-chrome">
              <div className="admin-content-chrome-text">
                <h1 className="admin-content-title">{contentMeta.title}</h1>
                {chromeDescription != null ? (
                  <p className="admin-content-description">{chromeDescription}</p>
                ) : null}
              </div>
            </header>
          ) : null}
          <div className="admin-content-body">
            <Outlet />
          </div>
          <AdminLegalFooter />
        </div>
      </main>

      {mobileOpen ? (
        <>
          <button
            type="button"
            className="admin-mobile-backdrop"
            aria-label="Close navigation menu"
            onClick={closeMobile}
          />
          <aside className="admin-mobile-sidebar" style={{ top: HEADER_H, width: EXPANDED_W }}>
            <div className="admin-sidebar-brand admin-sidebar-brand--mobile">
              <VeritrellisLogo height={26} />
            </div>
            <nav
              className="admin-sidebar-nav-inner admin-sidebar-nav-inner--mobile"
              aria-label="Main"
            >
              {visibleNav.map((item) =>
                renderNavItem(item, { showLabels: true, onNavigate: closeMobile })
              )}
            </nav>
          </aside>
        </>
      ) : null}
    </TooltipProvider>
  );
};

export const AdminLayout = () => (
  <AdminPageMetaProvider>
    <AdminLayoutInner />
  </AdminPageMetaProvider>
);
